import {
    Alert,
    Box,
    Button,
    Switch,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    HourglassEmptyOutlined,
    NotificationsActiveOutlined,
    SimCardDownloadOutlined,
} from '@mui/icons-material';
import {
    ACard,
    ADataGrid,
    ADataGridFilter,
    ASectionTitle,
    PagedSearchParams,
} from '@atiautomacao/ati-ui-library';
import {useSnackbar} from 'notistack';
import {
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {createPortal} from 'react-dom';
import {useAppSelector} from '../../../Config/Hooks';
import {AUTHORITIES} from '../../../Config/Constants';
import {hasPermission} from '../../../Shared/Auth/AuthenticationUtil';
import {extractFiltersFromColumns} from '../../../Utils/DataUitils';
import {Pill} from '../Equipments/PIll';
import {TeleobjectInhibitionDialog} from './TeleobjectInhibitionDialog';
import {
    TELEOBJECT_GRID_COLUMNS,
    downloadTeleobjectExport,
    toTeleobjectSearchRequest,
} from './TeleobjectsGrid';
import {
    useTeleobjectInhibitionSearch,
    useUpdateTeleobjectInhibition,
    useLiveAccountAuthorities,
} from './TeleobjectsReactQuery';
import {
    TELEOBJECT_SELECTION_LIMIT,
    TeleobjectInhibitionAction,
    TeleobjectInhibitionRow,
    TeleobjectPendingAction,
} from './Teleobjects.types';

export interface TeleobjectGridRow {
    id: number;
    teleObjectName: string;
    equipmentName: string;
    powerStationName: string;
    skidName: string;
    inhibited: ReactNode;
    actions?: ReactNode;
    selected?: boolean;
}

const toGridRow = (
    row: TeleobjectInhibitionRow,
    options: {
        canModify: boolean;
        actionsDisabled: boolean;
        selected: boolean;
        onRowAction: (
            teleObjectId: number,
            action: TeleobjectInhibitionAction,
        ) => void;
    },
): TeleobjectGridRow => {
    const enabled = !row.inhibited;
    const situation = enabled ? 'Habilitado' : 'Inibido';
    const actionLabel = enabled ? 'Inibir' : 'Habilitar';

    return {
        id: row.teleObjectId,
        teleObjectName: row.teleObjectName,
        equipmentName: row.equipmentName,
        powerStationName: row.powerStationName,
        skidName: row.skidName ?? '',
        selected: options.selected,
        inhibited: (
            <Pill
                label={situation}
                tone={enabled ? 'doneGreen' : 'openOrange'}
            />
        ),
        actions: (
            <Tooltip
                title={
                    options.canModify
                        ? `${actionLabel} ${row.teleObjectName}`
                        : 'Você não possui permissão para habilitar ou inibir teleobjetos'
                }
            >
                <span>
                    <Switch
                        checked={enabled}
                        disabled={!options.canModify || options.actionsDisabled}
                        onChange={() =>
                            options.onRowAction(
                                row.teleObjectId,
                                enabled ? 'INHIBIT' : 'ACTIVATE',
                            )
                        }
                        color="primary"
                        inputProps={{
                            'aria-label': `${actionLabel} ${row.teleObjectName}`,
                        }}
                    />
                </span>
            </Tooltip>
        ),
    };
};

export function TeleobjectsPage() {
    const theme = useTheme();
    const {enqueueSnackbar} = useSnackbar();
    const account = useAppSelector(state => state.authentication.account);
    const mainUpdateInterval = useAppSelector(
        state => state.userConfigurarion.mainUpdateInterval,
    );
    const [pagedSearchParams, setPagedSearchParams] = useState(
        new PagedSearchParams(
            extractFiltersFromColumns(TELEOBJECT_GRID_COLUMNS).map(filter => ({
                ...filter,
            })),
            0,
            10,
        ),
    );
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isClear, setIsClear] = useState(false);
    const [pendingAction, setPendingAction] =
        useState<TeleobjectPendingAction | null>(null);
    const [permissionRevoked, setPermissionRevoked] = useState(false);
    const visibleInhibitionRef = useRef<Map<number, boolean>>(new Map());
    const mutationInFlightRef = useRef(false);
    const gridFooterRef = useRef<HTMLDivElement>(null);
    const [paginationSpacer, setPaginationSpacer] = useState<Element | null>(
        null,
    );

    const searchRequest = useMemo(
        () =>
            toTeleobjectSearchRequest(
                pagedSearchParams.filters,
                pagedSearchParams.page,
                pagedSearchParams.size,
            ),
        [pagedSearchParams],
    );
    const searchQuery = useTeleobjectInhibitionSearch(
        searchRequest,
        mainUpdateInterval,
    );
    const liveAccountQuery = useLiveAccountAuthorities(mainUpdateInterval);
    const mutation = useUpdateTeleobjectInhibition();
    const hasMutationPermission = hasPermission(
        liveAccountQuery.data ?? account?.authoritySet,
        [
            AUTHORITIES.SYSADMIN,
            AUTHORITIES.TOGGLE_EQUIPMENT_INHIBITION,
        ],
    );
    const canModify = hasMutationPermission && !permissionRevoked;
    const sourceRows = searchQuery.data?.data ?? [];
    const totalOfRecords = searchQuery.data?.totalOfRecords ?? 0;
    const actionsDisabled = mutation.isLoading || selectedIds.length === 0;

    const openAction = useCallback(
        (action: TeleobjectInhibitionAction, teleObjectIds: number[]) => {
            if (!canModify) {
                enqueueSnackbar(
                    'Você não possui permissão para habilitar ou inibir teleobjetos.',
                    {variant: 'error'},
                );
                return;
            }
            setPendingAction({action, teleObjectIds});
        },
        [canModify, enqueueSnackbar],
    );

    const rows = useMemo(
        () =>
            sourceRows.map(row =>
                toGridRow(row, {
                    canModify,
                    actionsDisabled: mutation.isLoading,
                    selected: selectedIds.includes(row.teleObjectId),
                    onRowAction: (teleObjectId, action) =>
                        openAction(action, [teleObjectId]),
                }),
            ),
        [canModify, mutation.isLoading, openAction, selectedIds, sourceRows],
    );

    useEffect(() => {
        if (searchQuery.isLoading || searchQuery.isError) {
            return;
        }

        const previous = visibleInhibitionRef.current;
        const changedSelectedRow = sourceRows.some(
            row =>
                selectedIds.includes(row.teleObjectId) &&
                previous.has(row.teleObjectId) &&
                previous.get(row.teleObjectId) !== row.inhibited,
        );
        visibleInhibitionRef.current = new Map(
            sourceRows.map(row => [row.teleObjectId, row.inhibited]),
        );

        if (changedSelectedRow && !mutation.isLoading) {
            setSelectedIds([]);
            setIsClear(true);
            enqueueSnackbar(
                'A situação dos teleobjetos selecionados mudou. Revise os dados antes de continuar.',
                {variant: 'warning'},
            );
            return;
        }
        setIsClear(false);
    }, [
        enqueueSnackbar,
        mutation.isLoading,
        searchQuery.isError,
        searchQuery.isLoading,
        selectedIds,
        sourceRows,
    ]);

    useLayoutEffect(() => {
        setPaginationSpacer(
            gridFooterRef.current?.querySelector(
                '.MuiTablePagination-spacer',
            ) ?? null,
        );
    }, [searchQuery.isLoading, totalOfRecords, rows.length]);

    const handleOnFiltersChange = useCallback(
        (dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
            const nextFilters = dataGridFilters.map(filter => ({...filter}));
            const filtersChanged =
                nextFilters.length !== pagedSearchParams.filters.length ||
                pagedSearchParams.filters.some((filter, index) => {
                    const next = nextFilters[index];
                    return (
                        !next ||
                        filter.name !== next.name ||
                        filter.value !== next.value ||
                        filter.condition !== next.condition ||
                        filter.sort !== next.sort
                    );
                });

            setPagedSearchParams(
                new PagedSearchParams(nextFilters, page, pageSize),
            );
            if (filtersChanged) {
                setSelectedIds([]);
                setIsClear(true);
            }
        },
        [pagedSearchParams.filters],
    );

    const rowSelect = (data: TeleobjectGridRow[]) => {
        setSelectedIds(data.map(item => item.id));
    };

    const handleMaxSelected = (isMax: boolean) => {
        if (isMax) {
            enqueueSnackbar(
                'É possível selecionar no máximo 1.000 teleobjetos.',
                {variant: 'warning'},
            );
        }
    };

    const exportDisabled =
        searchQuery.isLoading || sourceRows.length === 0;

    const handleExport = (format: 'csv' | 'pdf') => {
        if (exportDisabled) {
            return;
        }
        downloadTeleobjectExport(sourceRows, format);
    };

    const confirmAction = async () => {
        if (
            !pendingAction ||
            mutation.isLoading ||
            mutationInFlightRef.current
        ) {
            return;
        }
        mutationInFlightRef.current = true;
        try {
            const result = await mutation.mutateAsync({
                action: pendingAction.action,
                teleObjectIds: pendingAction.teleObjectIds,
            });
            const completed =
                result.updatedIds.length + result.unchangedIds.length;
            if (result.failures.length === 0) {
                enqueueSnackbar(
                    `${completed} ${
                        completed === 1
                            ? 'teleobjeto concluído'
                            : 'teleobjetos concluídos'
                    } com sucesso.`,
                    {variant: 'success'},
                );
                setSelectedIds([]);
                setIsClear(true);
            } else {
                enqueueSnackbar(
                    `${completed} concluídos; ${result.failures.length} não alterados.`,
                    {variant: completed > 0 ? 'warning' : 'error'},
                );
                setSelectedIds(
                    result.failures.map(failure => failure.teleObjectId),
                );
                setIsClear(true);
            }
            setPendingAction(null);
        } catch (error) {
            const status =
                typeof error === 'object' &&
                error !== null &&
                'response' in error &&
                typeof error.response === 'object' &&
                error.response !== null &&
                'status' in error.response
                    ? error.response.status
                    : undefined;
            if (status === 403) {
                setPermissionRevoked(true);
                enqueueSnackbar(
                    'A permissão para alterar teleobjetos não está mais disponível.',
                    {variant: 'error'},
                );
            } else {
                enqueueSnackbar(
                    'Não foi possível concluir a alteração. Nenhum novo estado foi confirmado.',
                    {variant: 'error'},
                );
            }
            setPendingAction(null);
        } finally {
            mutationInFlightRef.current = false;
        }
    };

    const habilitarButton = (
        <Button
            disabled={actionsDisabled || !canModify}
            variant="text"
            onClick={() => openAction('ACTIVATE', selectedIds)}
            endIcon={<NotificationsActiveOutlined />}
            sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: 'text.primary',
            }}
        >
            HABILITAR
        </Button>
    );

    const inibirButton = (
        <Button
            variant="text"
            disabled={actionsDisabled || !canModify}
            onClick={() => openAction('INHIBIT', selectedIds)}
            endIcon={<HourglassEmptyOutlined />}
            sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: 'text.primary',
            }}
        >
            INIBIR
        </Button>
    );

    const exportButtons = (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mr: 1}}>
            <Button
                size="medium"
                variant="text"
                startIcon={<SimCardDownloadOutlined fontSize="large" />}
                onClick={() => handleExport('csv')}
                disabled={exportDisabled}
            >
                CSV
            </Button>
            <Button
                size="medium"
                variant="text"
                startIcon={<SimCardDownloadOutlined fontSize="large" />}
                onClick={() => handleExport('pdf')}
                disabled={exportDisabled}
            >
                PDF
            </Button>
        </Box>
    );

    return (
        <>
            <Box sx={{flex: 1, minWidth: 0, mb: 3, marginLeft: 3}}>
                <ASectionTitle
                    titleLabel="Inibe/Habilita Teleobjetos"
                    subTitleLabel="Gerencie, habilite e iniba teleobjetos"
                />
            </Box>
            {!canModify && (
                <Typography
                    aria-live="polite"
                    sx={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        p: 0,
                        m: -1,
                        overflow: 'hidden',
                        clip: 'rect(0 0 0 0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                    }}
                >
                    Você pode consultar os teleobjetos, mas não possui permissão
                    para habilitar ou inibir.
                </Typography>
            )}
            <Box sx={{marginLeft: 3}}>
                <ACard
                    styleProps={{
                        headerStyle: {height: 0, padding: 0},
                        cardStyle: {borderRadius: '0 0 8px 8px', width: '100%'},
                        contentStyle: {padding: 0},
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 3,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            height: 64,
                        }}
                    >
                        <Typography
                            variant="body2"
                            aria-live="polite"
                            sx={{
                                fontWeight: 500,
                                color:
                                    selectedIds.length > 0
                                        ? 'text.primary'
                                        : theme.palette.mode === 'dark'
                                            ? 'text.secondary'
                                            : '#6B7280',
                                fontSize: '14px',
                            }}
                        >
                            {selectedIds.length} itens selecionados
                        </Typography>

                        <Box sx={{display: 'flex', gap: 2}}>
                            {canModify ? (
                                habilitarButton
                            ) : (
                                <Tooltip title="Você não possui permissão para habilitar ou inibir teleobjetos">
                                    <span>{habilitarButton}</span>
                                </Tooltip>
                            )}
                            {canModify ? (
                                inibirButton
                            ) : (
                                <Tooltip title="Você não possui permissão para habilitar ou inibir teleobjetos">
                                    <span>{inibirButton}</span>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                    {searchQuery.isError ? (
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    color="inherit"
                                    onClick={() => searchQuery.refetch()}
                                >
                                    Tentar novamente
                                </Button>
                            }
                        >
                            Não foi possível carregar os teleobjetos.
                        </Alert>
                    ) : (
                        <Box
                            ref={gridFooterRef}
                            sx={{
                                position: 'relative',
                                '& .MuiTablePagination-spacer': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                },
                            }}
                        >
                            <ADataGrid
                                ariaLabel="communication-grid-teleobjects"
                                hideFilters={false}
                                showFilterOptions={false}
                                hideSelection={false}
                                onRowSelect={rowSelect}
                                maxRowSelected={TELEOBJECT_SELECTION_LIMIT}
                                onMaxRowSelected={handleMaxSelected}
                                columns={TELEOBJECT_GRID_COLUMNS}
                                rows={rows}
                                page={pagedSearchParams.page}
                                rowsPerPage={pagedSearchParams.size}
                                rowsPerPageOptionsDefault={[10, 25, 50]}
                                loading={searchQuery.isLoading}
                                totalOfRecords={totalOfRecords}
                                onFiltersChange={handleOnFiltersChange}
                                tableContainerStyle={{borderRadius: 0}}
                                onClearSelectedTempRows={isClear}
                            />
                            {paginationSpacer
                                ? createPortal(exportButtons, paginationSpacer)
                                : (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 16,
                                            bottom: 6,
                                            zIndex: 1,
                                        }}
                                    >
                                        {exportButtons}
                                    </Box>
                                )}
                        </Box>
                    )}
                </ACard>
                <TeleobjectInhibitionDialog
                    open={pendingAction !== null}
                    pendingAction={pendingAction}
                    saving={mutation.isLoading}
                    onCancel={() => setPendingAction(null)}
                    onConfirm={confirmAction}
                />
            </Box>
        </>
    );
}
